import { useState, type ReactNode } from "react";
import * as Sentry from "@sentry/react";

import { ShareDialog } from "@/domains/documents/components/organisms/ShareDialog";
import {
  useDocumentVerification,
  verifiedSelectionState,
} from "@/domains/documents/hooks/useDocumentVerification";
import { useChatStore } from "@/hooks/useChatStore";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { useToast } from "@/hooks/use-toast";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { bindTrackToCurrentUser } from "@/lib/posthog";
import { logger } from "@/utils/logger";
import type { SigningListItem } from "./types";
import { useFileDownload } from "./useFileDownload";

function selectionDocuments(items: SigningListItem[]) {
  return items.flatMap((item) =>
    item.documentId == null
      ? []
      : [
          {
            id: String(item.documentId),
            filename: item.title,
            filePath: item.filePath,
            project_id: item.projectId,
            verified_at: item.verifiedAt,
          },
        ]
  );
}

/** The Documents page's actions, bound to a set of Signing rows. */
export interface SigningDocumentActionSet {
  /** Containers among the targets — the count the actions act on. */
  count: number;
  onShare?: () => void;
  /** Undefined for a lone row with no stored file — the menu then drops the
   *  item instead of offering one that can only fail. */
  onDownload?: () => void;
  /** Undefined unless every target is writable. */
  onToggleVerified?: () => void;
  verifiedState: "all" | "none" | "mixed";
  onAskFranklin: () => void;
}

export interface SigningDocumentActions {
  /**
   * Bind the actions to `items`. A lone right-clicked row passes
   * `{ single: true }`: its download is that one file's own (single-file
   * copy, telemetry and error grouping), never the bulk path's. `onDone` runs
   * after an action that consumes
   * the targets (Share succeeded, Ask Franklin) — the bulk bar clears the
   * selection there; a single right-clicked row has nothing to clear.
   */
  actionsFor: (
    items: SigningListItem[],
    options?: { onDone?: () => void; single?: boolean }
  ) => SigningDocumentActionSet;
  /** Open the share dialog on document ids — the signature panel's Share.
   *  Undefined with no organisation, like every Share here. */
  shareDocuments?: (documentIds: string[]) => void;
  /** Mount once: the share dialog the actions open. */
  dialogs: ReactNode;
}

/**
 * Share, Download, Verified and Ask Franklin over Signing rows — each the one
 * the Documents page runs, fed the containers' document ids, since the Signing
 * list lives in react-query rather than in the document store's selection.
 * Shared by the bulk action bar and the rows' right-click menu so the two
 * cannot drift. Call from an observer: the verification gate reads the store.
 */
export function useSigningDocumentActions(buildingId: number): SigningDocumentActions {
  const documentStore = useDocumentStore();
  const chatStore = useChatStore();
  const { currentOrganization } = useOrganizationContext();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { canVerifyDocument, setVerified } = useDocumentVerification();
  const [isShareOpen, setIsShareOpen] = useState(false);
  // Kept apart from the open flag so the ids survive the dialog's close animation.
  const [shareTarget, setShareTarget] = useState<{ ids: string[]; onDone?: () => void }>({
    ids: [],
  });
  const orgId = currentOrganization?.id;

  const download = async (documents: ReturnType<typeof selectionDocuments>) => {
    const downloadable = documents.flatMap((doc) =>
      doc.filePath ? [{ id: doc.id, filename: doc.filename, file_path: doc.filePath }] : []
    );
    const skippedCount = documents.length - downloadable.length;
    if (skippedCount > 0) {
      toast({
        title: t.common.warning,
        description: t.signing.bulk.downloadSkipped.replace("{count}", String(skippedCount)),
        variant: "destructive",
      });
    }
    if (downloadable.length === 0) return;
    const trackDownload = bindTrackToCurrentUser(currentOrganization?.id);
    const reportFailure = (cause: unknown) => {
      const error = cause instanceof Error ? cause : new Error("Signing bulk download failed");
      const context = {
        building_id: buildingId,
        organization_id: currentOrganization?.id,
        selected_count: documents.length,
        downloadable_count: downloadable.length,
        skipped_count: skippedCount,
      };
      const sentryEventId = Sentry.captureException(error, {
        tags: { feature: "signing", action: "bulk-download" },
        contexts: { download: context },
        fingerprint: ["signing", "bulk-download"],
      });
      void logger.error("ui", "signing.bulk-download", "Signing bulk download failed", error, {
        ...context,
        sentry_event_id: sentryEventId,
      });
    };

    try {
      const downloaded = await documentStore.bulkDownload(downloadable, [], [], {
        surface: "signing",
      });
      if (downloaded && downloadable.length > 1) {
        trackDownload("document_downloaded", { file_ext: "zip", surface: "signing" });
      }
    } catch (error) {
      toast({ variant: "destructive", description: t.signing.panel.downloadFailed });
      reportFailure(error);
    }
  };

  const downloadFile = useFileDownload();

  const shareDocuments = (ids: string[], onDone?: () => void) => {
    setShareTarget({ ids, onDone });
    setIsShareOpen(true);
  };

  const singleDownload = (doc: ReturnType<typeof selectionDocuments>[number]) => {
    const { filePath, filename } = doc;
    return filePath ? () => void downloadFile(filePath, filename) : undefined;
  };

  const actionsFor = (
    items: SigningListItem[],
    { onDone, single = false }: { onDone?: () => void; single?: boolean } = {}
  ) => {
    const documents = selectionDocuments(items);
    const verifiedState = verifiedSelectionState(documents);
    // Offered only when every target is writable — a partially permitted bulk
    // write would half-succeed and leave the rest to RLS.
    const canVerify = documents.length > 0 && documents.every((doc) => canVerifyDocument(doc));

    return {
      count: documents.length,
      onShare: orgId
        ? () => {
            shareDocuments(
              documents.map((doc) => doc.id),
              onDone
            );
          }
        : undefined,
      onDownload:
        single && documents.length === 1
          ? singleDownload(documents[0])
          : documents.length > 0
            ? () => void download(documents)
            : undefined,
      onToggleVerified: canVerify
        ? () => void setVerified(documents, verifiedState !== "all")
        : undefined,
      verifiedState,
      onAskFranklin: () => {
        for (const doc of documents) {
          chatStore.addAttachedDocument({ id: doc.id, filename: doc.filename });
        }
        chatStore.setViewMode("sidebar");
        onDone?.();
      },
    };
  };

  const dialogs = orgId ? (
    <ShareDialog
      open={isShareOpen}
      onOpenChange={setIsShareOpen}
      onSuccess={() => shareTarget.onDone?.()}
      selectedDocumentIds={shareTarget.ids}
      buildingId={String(buildingId)}
      orgId={orgId}
    />
  ) : null;

  return {
    actionsFor,
    shareDocuments: orgId ? (ids) => shareDocuments(ids) : undefined,
    dialogs,
  };
}
